import { ALERT_PRIORITY_LABELS } from '../../../api/src/alerts/alerts.types';
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Paper,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { Box, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import { useWorkspace } from '../workspace-context';
import { useCanEdit } from '../components/EditorOnly';
import { api } from '../api/client';
import type {
  AlertAiInfo,
  AlertDetail,
  AlertPage,
  AlertSettings,
  AlertSyncView,
} from '../../../api/src/alerts/alerts.types';
import type { SearchCatalogProperty } from '../../../api/src/search-metrics/search-metrics.types';
import type { TelegramChannel } from '../api/types';
import { AlertsSettings } from './AlertsSettings';
import { AlertsPreview } from './AlertsPreview';
import { AlertsSource, AlertsSourceDialog } from './AlertsSource';

export const alertStatusNames: Record<string, string> = {
  review: 'Требует разбора',
  ignored: 'Без отправки',
  pending: 'В очереди',
  sending: 'Отправляется',
  retrying: 'Повтор после лимита',
  sent: 'Отправлено в Telegram',
  failed: 'Ошибка доставки',
  unknown: 'Доставка не подтверждена',
  cancelled: 'Отменено',
};
const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Ошибка запроса');
const time = (s: string | null, missing = 'Ещё не проверено') =>
  s ? new Date(s).toLocaleString('ru-RU') : missing;
const twoLines = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
} as const;
export function AlertsPage() {
  const { activeWorkspace } = useWorkspace();
  const editable = useCanEdit('alerts.settings', 'alerts.schedule', 'alerts.send');
  const canRun = useCanEdit('alerts.run', 'alerts.send');
  const canPreview = useCanEdit('alerts.run');
  const canViewTelegram = useCanEdit('telegram.view');
  const canViewMetrics = useCanEdit('metrics.view');
  const w = activeWorkspace!.id;
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<AlertSettings | null>(null);
  const [aiInfo, setAiInfo] = useState<AlertAiInfo | null>(null);
  const [channels, setChannels] = useState<TelegramChannel[]>([]);
  const [properties, setProperties] = useState<SearchCatalogProperty[]>([]);
  const [sync, setSync] = useState<AlertSyncView[]>([]);
  const [result, setResult] = useState<AlertPage | null>(null);
  const [detail, setDetail] = useState<AlertDetail | null>(null);
  const [sourceEvent, setSourceEvent] = useState<{ workspace: string; id: string } | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSourceEvent(null);
  }, [w]);
  const savedRule = detail?.settings.rules?.find((rule) => rule.id === detail.ruleId);
  const refresh = useCallback(async () => {
    const [s, c, p, y, ai] = await Promise.all([
      api.alertSettings(w),
      canViewTelegram ? api.telegram(w) : Promise.resolve([]),
      canViewMetrics ? api.searchProperties(w) : Promise.resolve([]),
      api.yandexAlertStatus(w),
      api.alertAiInfo(w),
    ]);
    setSettings(s);
    setAiInfo(ai);
    setChannels(c);
    setProperties(p.filter((p) => p.provider === 'yandex'));
    setSync(y);
  }, [w, canViewTelegram, canViewMetrics]);
  useEffect(() => {
    void refresh().catch((e) => setError(errorMessage(e)));
  }, [refresh]);
  useEffect(() => {
    let active = true;
    setResult(null);
    void api
      .alerts(w, page, status)
      .then((r) => {
        if (active) setResult(r);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [w, page, status, notice]);
  const action = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PageHeader
        title="Алерты"
        description="AI-анализ писем, диагностика сайтов и история доставки в Telegram."
        action={
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() =>
              void action(async () => {
                await refresh();
                setResult(await api.alerts(w, page, status));
              })
            }
          >
            Обновить
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      {settings && !settings.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Доставка в Telegram выключена. События сохраняются в журнале.
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(_, v: number) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Журнал" />
        <Tab label="Источники и AI-промпт" />
        <Tab label="Проверить письмо" />
      </Tabs>
      {tab === 0 && (
        <Stack gap={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between">
            <TextField
              select
              label="Состояние"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">Все события</MenuItem>
              {Object.entries(alertStatusNames).map(([key, name]) => (
                <MenuItem key={key} value={key}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            {canRun && settings?.yandex.source === 'api' && (
              <Button
                disabled={busy || !settings?.yandex.enabled}
                onClick={() =>
                  void action(async () => {
                    await api.syncYandexAlerts(w);
                    setNotice(
                      'Проверка Яндекса поставлена в очередь. Нажмите «Обновить», чтобы увидеть результат.',
                    );
                  })
                }
              >
                Проверить Яндекс сейчас
              </Button>
            )}
          </Stack>
          {settings?.yandex.source === 'mail' && (
            <Alert severity="info">
              Яндекс: выбран приём писем через Gmail и n8n
              {settings.yandex.enabled ? '' : ' (выключен)'}. API диагностики не опрашивается.
              Отсутствие писем не подтверждает отсутствие проблем.
            </Alert>
          )}
          {!result ? (
            <CircularProgress aria-label="Загрузка алертов" />
          ) : !result.total ? (
            <Paper variant="outlined" sx={{ p: 4 }}>
              <Typography variant="h6">Событий пока нет</Typography>
              <Typography color="text.secondary">
                Подключите источники и проверьте AI-промпт. Отсутствие событий не подтверждает
                отсутствие проблем на сайтах.
              </Typography>
              <Button onClick={() => setTab(1)} sx={{ mt: 2 }}>
                Настроить источники
              </Button>
            </Paper>
          ) : (
            <>
              <Typography color="text.secondary">Событий: {result.total}</Typography>
              {result.items.map((item) => {
                const summary = item.analysis?.result?.summary;
                const showReason =
                  !summary ||
                  item.action === 'review' ||
                  ['review', 'failed', 'unknown', 'retrying', 'cancelled'].includes(item.status) ||
                  !Object.hasOwn(alertStatusNames, item.status);
                const diagnostics = [
                  ...new Set([showReason ? item.reason : null, item.error]),
                ].filter((message): message is string => Boolean(message));
                return (
                  <Paper key={item.id} component="article" variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Box sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>
                        <Stack
                          direction="row"
                          gap={0.75}
                          alignItems="center"
                          flexWrap="wrap"
                          mb={0.5}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {item.provider === 'gsc'
                              ? 'GSC · Gmail'
                              : item.provider === 'yandex'
                                ? item.source === 'api'
                                  ? 'Яндекс · API'
                                  : 'Яндекс · Gmail'
                                : item.provider === 'other'
                                  ? 'Другой сервис · Gmail'
                                  : 'Источник не распознан'}
                          </Typography>
                          <Chip
                            size="small"
                            color={
                              item.status === 'sent'
                                ? 'success'
                                : ['failed', 'unknown'].includes(item.status)
                                  ? 'error'
                                  : item.status === 'review'
                                    ? 'warning'
                                    : 'default'
                            }
                            label={alertStatusNames[item.status] ?? item.status}
                          />
                          {item.priority && (
                            <Chip
                              size="small"
                              label={`${ALERT_PRIORITY_LABELS[item.priority]}${item.analysis?.result ? ' · AI' : ''}`}
                            />
                          )}
                          <Typography
                            component="time"
                            dateTime={item.receivedAt}
                            variant="caption"
                            color="text.secondary"
                            aria-label={`Получено: ${time(item.receivedAt, 'время не указано')}`}
                          >
                            {time(item.receivedAt, 'Время получения не указано')}
                          </Typography>
                        </Stack>
                        <Typography component="h2" variant="body1" fontWeight={700} sx={twoLines}>
                          {item.site ? `${item.site} · ` : ''}
                          {item.subject}
                        </Typography>
                        {summary && (
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            sx={{ ...twoLines, mt: 0.5 }}
                          >
                            AI: {summary}
                          </Typography>
                        )}
                        {diagnostics.map((message) => (
                          <Typography
                            key={message}
                            color={message === item.error ? 'error' : 'text.primary'}
                            variant="body2"
                            sx={{ mt: 0.5 }}
                          >
                            {message}
                          </Typography>
                        ))}
                      </Box>
                      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => setSourceEvent({ workspace: w, id: item.id })}
                        >
                          Исходник
                        </Button>
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            void action(async () => setDetail(await api.alert(w, item.id)))
                          }
                        >
                          Подробнее
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
              <Pagination
                page={page}
                count={Math.ceil(result.total / 50)}
                onChange={(_, p) => setPage(p)}
              />
            </>
          )}
        </Stack>
      )}
      {tab === 1 &&
        (settings ? (
          <AlertsSettings
            workspace={w}
            key={settings.revision}
            settings={settings}
            aiInfo={aiInfo}
            channels={channels}
            properties={properties}
            editable={editable}
            busy={busy}
            yandexStatus={
              settings.yandex.source === 'api' &&
              settings.yandex.propertyIds.length > 0 && (
                <Stack gap={2}>
                  <Typography fontWeight={700}>Состояние проверок API Яндекса</Typography>
                  {settings.yandex.propertyIds.map((id) => {
                    const row = sync.find((s) => s.propertyId === id);
                    return (
                      <Paper key={id} variant="outlined" sx={{ p: 2 }}>
                        <Typography fontWeight={700}>
                          {properties.find((p) => p.id === id)?.label ?? id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Успешная проверка: {time(row?.lastSuccessAt ?? null)}
                        </Typography>
                        {row?.error && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {row.error}
                          </Alert>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              )
            }
            onSave={(draft) =>
              void action(async () => {
                setSettings(await api.saveAlertSettings(w, draft));
                setNotice(
                  'Настройки сохранены. Они применяются к новым событиям; прежние события не переотправляются.',
                );
              })
            }
          />
        ) : (
          <CircularProgress />
        ))}
      {tab === 2 && <AlertsPreview workspace={w} editable={canPreview} />}
      {sourceEvent?.workspace === w && (
        <AlertsSourceDialog
          key={sourceEvent.id}
          workspace={w}
          eventId={sourceEvent.id}
          onClose={() => setSourceEvent(null)}
        />
      )}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Свидетельства события</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack gap={2} sx={{ overflowWrap: 'anywhere' }}>
              <Typography fontWeight={700}>{detail.subject}</Typography>
              <Typography variant="body2">
                Сайт: {detail.site ?? 'Привязка не определена'} · Отправитель:{' '}
                {detail.sender ?? 'не указан'}
              </Typography>
              <Typography variant="body2">
                Получено: {time(detail.receivedAt, 'время не указано')} · Записано в журнал:{' '}
                {time(detail.createdAt, 'время не указано')}
              </Typography>
              {detail.analysis?.result && (
                <>
                  <Typography variant="body2">
                    Категория: {detail.analysis.result.category ?? 'не определена'}
                  </Typography>
                  <Typography variant="body2">AI: {detail.analysis.result.summary}</Typography>
                </>
              )}
              <Typography>{detail.reason}</Typography>
              {detail.error && <Alert severity="warning">{detail.error}</Alert>}
              <Typography variant="body2">
                {detail.analysis ? 'AI-анализ' : `Правило: ${detail.ruleId ?? 'не определено'}`} ·
                настройки v{detail.settingsRevision} ·{' '}
                {alertStatusNames[detail.status] ?? detail.status}
              </Typography>
              {detail.analysis && (
                <>
                  <Typography variant="body2">
                    Модель: {detail.analysis.model} · Ответ:{' '}
                    {detail.analysis.responseId ?? 'не получен'}
                    {detail.analysis.usage &&
                      ` · Токены: ${detail.analysis.usage.inputTokens} вход / ${detail.analysis.usage.outputTokens} выход`}
                  </Typography>
                  {detail.analysis.result && (
                    <Box
                      component="pre"
                      sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}
                    >
                      {JSON.stringify(detail.analysis.result, null, 2)}
                    </Box>
                  )}
                  <Typography fontWeight={700}>Промпт на момент анализа</Typography>
                  <Box
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}
                  >
                    {detail.settings.ai?.prompt ?? 'Не сохранён'}
                  </Box>
                </>
              )}
              {savedRule && (
                <Typography variant="body2">
                  Фразы в сохранённом правиле: {savedRule.subjectIncludes.join(' · ')}
                </Typography>
              )}
              <Typography variant="body2">
                Получатель: {detail.chatId ?? 'не выбран'} · Telegram message ID:{' '}
                {detail.messageId ?? 'нет подтверждения'}
              </Typography>
              <Typography fontWeight={700}>Исходные данные</Typography>
              <AlertsSource key={detail.id} evidence={detail.evidence} source={detail.source} />
              <Button onClick={() => setDetail(null)}>Закрыть</Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
